import React from 'react';
import type { RoutePath } from '@shared/core/router.helper/useRouterHelper';
import { RoutesProvider } from '@shared/core';
import { CREATE_WEDDING_APP_NAME } from '@apps/acquisitionFunnels/createWedding/constants';
import { CREATE_BABY_REGISTRY_APP_NAME } from '@apps/acquisitionFunnels/createBabyRegistry/constants';
import { SERVICE_CENTER_APP_NAME } from '@apps/card/serviceCenter/constants';
import { EventRoute } from './EventRoute';
import { getEcardType } from '@apps/admin/saveTheDate/SaveTheDate.utils';
import EcardError from '@shared/components/EmailsAndEcards/components/Error';
import { SAVE_THE_DATE_PHOTO_ECARD_TYPE } from '@apps/admin/saveTheDate/constants';
import { CardsRoutesProvider } from '@apps/card/Card.routes';
import { LazyLogoutPage } from '@apps/admin/logout/LogoutPage.lazy';

// Shared loadables (used in both CSR and SSR)
import { LazyAdmin } from '@apps/admin/Admin.lazy';
import { LazyGuest } from '@apps/guest/Guest.lazy';
import { LazyAccount } from '@apps/account/Account.lazy';
import { LazyShop } from '@apps/registry/public/routes/Shop/Shop.lazy';
import { LazyAccountMe } from '@apps/admin/accountMe/AccountMe.lazy';
import { LazyCreateWedding } from '@apps/acquisitionFunnels/createWedding/CreateWedding.lazy';
import { LazyCreateBabyRegistry } from '@apps/acquisitionFunnels/createBabyRegistry/CreateBabyRegistry.lazy';
import { LazyEventRedirect } from '@apps/admin/routes/EventRedirect/EventRedirect.lazy';
import { LazyEcardGuest } from '@apps/ecard/guest/EcardGuest.lazy';
import { LazySaveTheDateGuest } from '@apps/admin/saveTheDate/guest/SaveTheDateGuest.lazy';
import { LazyServiceCenter } from '@apps/card/serviceCenter/ServiceCenter.lazy';
import { LazyPublicShop } from '@apps/card/routes/PublicShop/PublicShop.lazy';
import { LazyCreateGeneralEvent } from '@apps/acquisitionFunnels/createGeneralEvent/CreateGeneralEvent.lazy';
import { LazyPublicStays } from '@apps/stays/PublicStays/PublicStays.lazy';
import { LazyRegistryGuest } from '@apps/registry/guest/RegistryGuest.lazy';

// SSR-only loadables
import { LazyContactCollectorGuest } from '@apps/guest/contactCollector/ContactCollectorGuest.lazy';
import { LazyRecipientPremiumEcardView } from '@apps/card/routes/RecipientPremiumEcardView/RecipientPremiumEcardView.lazy';
import { LazyPageNotFound } from '@shared/components/PageNotFound/ssr/PageNotFoundSSR.lazy';

const ShopComponent = () => {
  return (
    <RoutesProvider>
      <LazyShop />
    </RoutesProvider>
  );
};

const ContactCollectorGuestWrapper = ({ magicLinkId }: { magicLinkId: string }) => {
  return (
    <RoutesProvider>
      <LazyContactCollectorGuest magicLinkId={magicLinkId} />
    </RoutesProvider>
  );
};

export const ssrRoutes: Array<RoutePath<{ eventHandle: string; magicLinkId: string; ecardId: string }>> = [
  {
    path: '/404',
    component: () => <LazyPageNotFound />,
  },
  {
    path: '/contact/:magicLinkId',
    component: ({ match }) => (match ? <ContactCollectorGuestWrapper magicLinkId={match.params.magicLinkId} /> : null),
  },
  {
    path: '/account/(events|personal-info)',
    component: () => <LazyAccountMe appUrl={'account'} />,
  },
  {
    path: '/account',
    component: () => <LazyAccount appUrl="account" />,
  },
  {
    path: '/shipping-policy',
    // This route will only render if the feature flag is provided
    component: () => <ShopComponent />,
  },
  {
    path: '/return-policy',
    // This route will only render if the feature flag is provided
    component: () => <ShopComponent />,
  },
  {
    path: '/card/:ecardId(\\w{10})',
    component: ({ match }) => (
      <CardsRoutesProvider>
        <LazyRecipientPremiumEcardView orderNumber={match.params.ecardId} />
      </CardsRoutesProvider>
    ),
  },
  {
    path: '/cards',
    component: () => <LazyPublicShop />,
  },
  {
    path: '/shop',
    // This route will only render if the feature flag is provided
    component: () => <ShopComponent />,
  },
  {
    path: `/${CREATE_WEDDING_APP_NAME}`,
    component: () => <LazyCreateWedding />,
  },
  {
    path: `/${CREATE_BABY_REGISTRY_APP_NAME}`,
    component: () => <LazyCreateBabyRegistry />,
  },
  {
    path: '/ecard',
    component: ({ location }) => {
      const ecardType = getEcardType(location);
      return ecardType === undefined ? <EcardError /> : ecardType === SAVE_THE_DATE_PHOTO_ECARD_TYPE ? <LazySaveTheDateGuest /> : <LazyEcardGuest />;
    },
  },
  {
    path: '/createevent',
    component: () => <LazyCreateGeneralEvent />,
  },
  {
    path: '/event',
    component: () => <LazyEventRedirect />,
  },
  {
    path: '/travel',
    component: () => <LazyPublicStays />,
  },
  {
    path: `/${SERVICE_CENTER_APP_NAME}`,
    component: () => <LazyServiceCenter />,
  },
  {
    path: '/logout',
    component: () => <LazyLogoutPage />,
  },
  {
    path: '/:eventHandle/edit',
    component: ({ match }) => (
      <EventRoute
        eventHandle={match.params.eventHandle}
        ComponentToRender={LazyAdmin}
      />
    ),
  },
  {
    path: '/:eventHandle/registry',
    component: ({ match }) => (
      <EventRoute
        eventHandle={match.params.eventHandle}
        ComponentToRender={LazyRegistryGuest}
      />
    ),
  },
  {
    path: '/:eventHandle',
    component: ({ match }) => (
      <EventRoute eventHandle={match.params.eventHandle}>
        <LazyGuest />
      </EventRoute>
    ),
  },
];
